import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'
import { NavLink } from 'react-router-dom'
import apis from 'browser/app/models/apis'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { CustomFormulas } from 'shared-libs/helpers/formulas'
import { evaluateFilters } from 'shared-libs/helpers/evaluation'
import { EntityDataSource } from 'browser/components/atomic-elements/organisms/entity/entity-data-source'
import 'browser/components/atomic-elements/organisms/side-navigation-bar-item/_side-navigation-bar-item.scss'

/**
 * @uiComponent
 */
interface ISideNavigationBarItemProps extends IBaseProps {
  count?: number
  to: string
  search: string
  label: string
  showBadge?: boolean
  recordBadgeAtKey?: (key: string, remove: boolean) => void
  isClosed: boolean
  _key: string
}

const VIEW_URL_RE = new RegExp("/view/(?<viewId>[a-zA-Z0-9-]+)")

export class SideNavigationBarItem extends React.Component<ISideNavigationBarItemProps, any> {
  private dataSet?: EntityDataSource

  public static defaultProps: Partial<ISideNavigationBarItemProps> = {
    count: 0,
    showBadge: false,
  }

  constructor(props) {
    super(props)
    this.state = {
      count: 0,
      view: null
    }
  }

  public componentDidMount() {
    this.configureBadging(this.props)
  }

  public componentDidUpdate(prevProps) {
    if (prevProps.to != this.props.to) {
      this.configureBadging(this.props)
    }
  }

  private configureBadging(props) {
    const viewId = this.getViewId(props)
    if (this.props.showBadge && viewId) {
      apis.getStore().findRecord(viewId)
        .then((view) => {
          if (this.getViewId(this.props) !== viewId) {
            return
          }

          this.setState({ view })
          this.initDataSet(view)
        })
    }
  }

  private getViewId(props) {
    return !_.isEmpty(props.to) && VIEW_URL_RE.exec(props.to)?.groups?.viewId
  }

  public componentWillUnmount() {
    this.dataSet?.dispose()
    this.props.recordBadgeAtKey?.(this.props._key, true)
  }

  public render() {
    const { to, label, isClosed } = this.props
    return (
      <NavLink
        activeClassName='is-active'
        className={`c-sideNavigationBarItem ${isClosed && 'u-hide'}`}
        data-debug-id={`sideNavItem:${label}`}
        to={{
          pathname: to,
        }}
      >
        <div className='u-flex u-flexGrow u-alignItemsCenter'>
          <div className='c-sideNavigationBarItem-label u-flexGrow'>
            {label}
          </div>
          {this.renderCount()}
        </div>
      </NavLink>
    )
  }

  private initDataSet = async (viewEntity) => {
    this.dataSet?.dispose()
    this.dataSet = undefined

    const settings = apis.getSettings()
    const viewState = _.get(viewEntity, 'view.state', {})
    const query = _.find(viewState.queries, q => _.get(q, 'useForSidenav')) || _.values(viewState.queries)[0]
    const store = apis.getStore()
    if (_.isNil(store.getRecord(query.entityType))) {
      await store.resolveEntitiesByUri([query.entityType])
    }

    const rawFilters = _.isEmpty(query.filters) ? viewState.filters : query.filters
    const filters = evaluateFilters(rawFilters, { settings, ...CustomFormulas })
    const debug = { context: `sidenavBlueDot--${viewEntity.displayName}` }
    this.dataSet = new EntityDataSource({...query, filters, debug })
    this.dataSet.query.setSize(1)
    this.dataSet.setOnChange(() => {
      this.handleDataSetChange(this.dataSet)
    })
    this.dataSet.find()
    this.setState(
      {
        count: this.getBadgeCount(this.dataSet)
      }
    )
  }

  private getBadgeCount(dataSet: EntityDataSource) {
    return _.get(dataSet, 'result.metadata.totalEntityMatchCount')
  }

  private renderCount() {
    const { count } = this.state
    if (count > 0) {
      const isCountMinimal = false
      return (
        <div className={classNames('c-sideNavigationBarItem-count u-positionRelative', {
          'c-sideNavigationBarItem-count--minimal': isCountMinimal,
        })}>
          {count}
        </div>
      )
    }
  }

  private handleDataSetChange = (dataSet) => {
    const { view } = this.state
    const viewState = view.view.state
    const query = _.values(viewState.queries)[0]
    query.groups = dataSet.query.groups
    query.orders = dataSet.query.orders
    const count = this.getBadgeCount(dataSet)
    this.setState(
      {
        view: view,
        count
      }
    )

    const badgeKey = `${this.props._key} | ${this.props.to} | ${this.props.label}`
    this.props.recordBadgeAtKey?.(badgeKey, count == 0)
  }
}
